
import { Component, Vue, Watch } from "vue-property-decorator";
import { API_ROUTES } from "@/utils/API";
import { SMApp, SMCases, SMUser } from "@/store";
import { API_TCaseDashboardWidget } from "@/models/case_dashboard";
import {
  CANDIDATE_ASSESSMENT_SERVICE_TYPE,
  SKILLED_WORKER_COS_SERVICE_TYPE,
  SKILLED_WORKER_SERVICE_TYPE,
  SPONSOR_LICENCE_SERVICE_TYPE,
  COMPLETE_SERVICE_STATUS,
  REPORT_GENERATED_SERVICE_STATUS,
  SKILLED_WORKER_COS_INSIDE_SERVICE_TYPE,
  SKILLED_WORKER_INSIDE_SERVICE_TYPE,
  EC_COMPLETED_STATUSES,
} from "@/constants/services";

import Summary from "@/components/Widgets/Candidate/Summary.vue";
import Progress from "@/components/Widgets/Candidate/Progress.vue";

import Highlight from "@/components/Widgets/Expert/Highlight.vue";
import HighlightCandidateOrEmployer from "@/components/Widgets/Highlights/Highlight.vue";
import AdvisorNotes from "@/components/Widgets/Expert/AdvisorNotes.vue";
import CaseCompletion from "@/components/Widgets/CaseCompletion.vue";
import ExpertTasks from "@/components/Widgets/Tasks/ExpertTasks.vue";
import EmployerTasks from "@/components/Widgets/Tasks/EmployerTasks.vue";
import CandidateTasks from "@/components/Widgets/Tasks/CandidateTasks.vue";
import { API_TCase } from "@/models/cases";

const NOTE_WIDGET_DEFAULT_OPTIONS = {
  name: "Advisor Notes",
  case_dashboard_id: 0,
  columns: 4,
  index: 1,
  widget_type: "advisor_notes",
};

const COL_SPAN_CLASS = "col-span";

const ICON_ARCHIVE = "archive-outline";

Component.registerHooks(["beforeRouteEnter", "beforeRouteUpdate"]);

@Component({
  components: {
    Loader: () => import("@/components/Loader/Loader.vue"),
    NotificationDataNotLoaded: () =>
      import("@/components/Notifications/NotificationDataNotLoaded.vue"),
    ServiceNotification: () =>
      import("@/modules/Services/Components/Notification/ServiceNotification.vue"),
    NotificationCosPlusSwStarted: () =>
      import("@/components/Notifications/NotificationCosPlusSwStarted.vue"),
    NotificationServiceEmployeeStarted: () =>
      import("@/components/Notifications/NotificationServiceEmployeeStarted.vue"),
    NotificationSlStarted: () => import("@/components/Notifications/NotificationSlStarted.vue"),
    ArchiveCandidate: () =>
      import("@/modules/Services/Components/ArchiveCandidate/ArchiveCandidate.vue"),
    Highlight,
    HighlightCandidateOrEmployer,
    CaseCompletion,
    AdvisorNotes,
    ExpertTasks,
    EmployerTasks,
    CandidateTasks,
  },
})
export default class VTemplateDashboard extends Vue {
  icon = ICON_ARCHIVE;

  headerWidgets: API_TCaseDashboardWidget[] = [] as API_TCaseDashboardWidget[];

  classes(columns: number) {
    if (this.isExpert) return `${COL_SPAN_CLASS}-12`;

    return `${COL_SPAN_CLASS}-${columns}`;
  }

  dashboardIsUpdated = false;

  async mounted() {
    if (!this.dashboardIsEmpty) return;

    if (this.dashboardIsUpdated) return;

    if (!this.caseId) return;

    await this.fetchCase(this.caseId);

    this.dashboardIsUpdated = true;
  }

  get isExpert() {
    return SMUser.isExpert;
  }

  get isCandidate() {
    return SMUser.isCandidate;
  }

  get isEmployer() {
    return SMUser.isEmployer;
  }

  get childServiceId(): number {
    const service = SMCases.case as API_TCase;

    return Number(service?.child_case_id ?? 0);
  }

  @Watch("childServiceId", { immediate: true })
  childServiceIdChanged(val: number) {
    if (!val) return;

    this.fetchChildService();
  }

  get loading() {
    return SMCases.loading;
  }

  get dashboardKey() {
    return SMCases.dashboardKey;
  }

  get dashboard() {
    return SMCases.dashboard;
  }

  get serviceLayoutWidgets() {
    return SMCases.serviceLayoutWidgets;
  }

  childService = {};

  async fetchChildService() {
    const id = this.childServiceId;

    if (!id) return;

    const { data } = await this.$http.get(API_ROUTES.CASE_ID(id));

    if (!data) return;

    this.childService = data;
  }

  get isServiceEmployeeStarted() {
    const service = SMCases.case as API_TCase;

    if (!service) return false;

    return (
      !!SMUser.isEmployee &&
      [COMPLETE_SERVICE_STATUS, REPORT_GENERATED_SERVICE_STATUS].includes(service.status)
    );
  }

  get showServiceEmployeeStartedNotification() {
    return this.isServiceEmployeeStarted && !SMCases.isDependantCase && !SMCases.isExtension;
  }

  get isCosPlusSwStarted() {
    if (!this.childServiceId) return false;

    const service = this.childService as API_TCase;

    if (!service) return false;

    const availableServiceTypes = [
      SKILLED_WORKER_SERVICE_TYPE,
      SKILLED_WORKER_INSIDE_SERVICE_TYPE,
      SKILLED_WORKER_COS_SERVICE_TYPE,
      SKILLED_WORKER_COS_INSIDE_SERVICE_TYPE,
    ];

    return availableServiceTypes.includes(service.case_template?.case_type);
  }

  get isSlStarted() {
    if (!this.childServiceId) return false;

    const service = this.childService as API_TCase;

    if (!service) return false;

    return service.case_template?.case_type === SPONSOR_LICENCE_SERVICE_TYPE;
  }

  get needsUpdate() {
    return SMCases.widgetNeedsUpdate;
  }

  get caseType() {
    return SMCases.caseType;
  }

  get isCandidateAssessment() {
    return this.caseType === CANDIDATE_ASSESSMENT_SERVICE_TYPE;
  }

  expertWidgetTypes = {
    highlight: Highlight,
    case_completion: CaseCompletion,
    tasks: ExpertTasks,
    advisor_notes: AdvisorNotes,
    "": null,
  };

  candidateWidgetTypes = {
    summary: Summary,
    highlight: HighlightCandidateOrEmployer,
    progress: Progress,
    case_completion: CaseCompletion,
    tasks: CandidateTasks,
    default: null,
  };

  employerWidgetTypes = {
    summary: Summary,
    highlight: HighlightCandidateOrEmployer,
    progress: Progress,
    case_completion: CaseCompletion,
    tasks: EmployerTasks,
    default: null,
  };

  employeeWidgetTypes = {
    summary: Summary,
    highlight: HighlightCandidateOrEmployer,
    progress: Progress,
    case_completion: CaseCompletion,
    tasks: CandidateTasks,
    default: null,
  };

  widgetTypes = {
    expert: this.expertWidgetTypes,
    candidate: this.candidateWidgetTypes,
    employer: this.employerWidgetTypes,
    employee: this.employeeWidgetTypes,
  };

  getWidgetType(widgetType: string) {
    if (!SMUser.user?.role) return null;

    return (this.widgetTypes[SMUser.user.role] as any)[widgetType];
  }

  noteWidget: API_TCaseDashboardWidget = NOTE_WIDGET_DEFAULT_OPTIONS;

  beforeCreate() {
    SMApp.hideNotes();
  }

  get dashboardIsEmpty() {
    return !this.dashboard || !Object.keys(this.dashboard).length;
  }

  @Watch("dashboardIsEmpty", { immediate: true })
  dashboardIsEmptyChanged(val: boolean) {
    if (!val) return;

    if (this.isExpert) this.reorganiseWidgets();
  }

  get noteActive() {
    return SMApp.noteActive;
  }

  @Watch("noteActive")
  noteActiveChanged(isActive: boolean) {
    if (this.isExpert) this.toggleNotes(isActive);
  }

  toggleNotes(isActive: boolean) {
    const zeroWidget = this.headerWidgets.find((item) => item.index === 0);

    if (!zeroWidget) return;

    if (isActive) {
      this.headerWidgets.push(this.noteWidget);
      zeroWidget.columns = 8;
    } else {
      this.headerWidgets.pop();
      zeroWidget.columns = 12;
    }
  }

  reorganiseWidgets() {
    const zeroWidget = this.serviceLayoutWidgets.find((item) => item.index === 0);

    if (zeroWidget) {
      zeroWidget.columns = 12;
      this.headerWidgets = [zeroWidget];
    }

    const widgets = this.serviceLayoutWidgets.filter((item) => item.index !== 0);

    widgets.map((item, index) => {
      item.index = index + 2;
    });

    SMCases.setServiceLayoutWidgets(widgets);
  }

  get serviceId() {
    return Number(this.$route.params.id || 0);
  }

  get onlineApplicationStatus() {
    return SMCases.onlineApplicationStatus;
  }

  atUpdate() {
    SMCases.setWidgetNeedsUpdate(false);
  }

  get caseId(): number {
    return Number(this.$route.params.id ?? 0);
  }

  async fetchCase(newCaseId: number) {
    SMCases.setLoading(true);

    try {
      await SMCases.fetchCaseTabs(newCaseId);

      await this.$nextTick();

      await SMCases.fetchDashboard(newCaseId);
    } finally {
      SMCases.setLoading(false);
    }
  }

  async fetchDashboard() {
    if (!SMUser.isValidationPassed && !SMUser.isExpert) return;

    if (!this.caseId && !SMCases.caseId) {
      const id = Number(localStorage.getItem("case_id"));

      if (!id) return;

      return await this.fetchCase(id);
    }

    if (!this.caseId) return;

    await this.fetchCase(this.caseId);
  }

  get isECComplete() {
    return EC_COMPLETED_STATUSES.includes(SMCases.caseStatus);
  }

  get canArchive(): boolean {
    if (SMUser.isCandidate || SMUser.isEmployee) return false;

    if (!this.isECComplete) return false;

    if (SMCases.isArchived) return false;

    return this.isCandidateAssessment && !this.isCosPlusSwStarted;
  }
}
